"use client";

import { MailQuestion } from "lucide-react";

import { SiteHeader } from "@/components/shell/site-header";

import { authRepository } from "@/features/auth/api/auth-repository";
import type { RequestScenario } from "@/features/auth/components/auth-request-content";
import { useEmailRequestPage } from "@/features/auth/components/auth-request-model";
import {
  BackToLogin,
  CTA_BASE,
  FIELD_BASE,
  RequestNotice,
  Spinner,
} from "@/features/auth/components/auth-request-shared";
import { RESEND_COPY } from "@/features/auth/components/resend/resend-content";

/**
 * `/signup/resend_activation` — the "send me the activation email again" screen.
 *
 * Promoted from the four-way design bake-off ("Split panel", Railway) once it
 * was approved; A, C and D and the `?variant=` harness are gone. They stay in
 * `d2435ba` if the comparison is ever wanted again.
 *
 * A full-bleed two-column split: a plum brand half carrying an oversized mail
 * glyph and the heading, and a working half carrying the message and the form.
 * Both halves run to the viewport edges, so there is no dead space at any width.
 *
 * This is the same shape `/login`, `/signup` and `/signup/activate` use, so all
 * four screens in the sign-up journey now share one composition — which is the
 * reason this candidate won.
 *
 * No footer, matching `/signup/activate` and the site-wide rule that v5 has none
 * anywhere. The page carries the real site nav rather than `<AuthShell>`.
 */
export function ResendView({ scenario }: { scenario: RequestScenario }) {
  const { email, setEmail, state, notice, busy, done, onSubmit } =
    useEmailRequestPage({
      scenario,
      copy: RESEND_COPY,
      submit: (email) => authRepository.resendActivation(email),
    });

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background font-sans text-foreground">
      <SiteHeader />

      <div className="grid flex-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <aside
          className="relative flex flex-col overflow-hidden px-8 py-14 lg:px-14"
          style={{
            background:
              "hsl(var(--card))",
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-px lg:block"
            style={{
              background:
                "hsl(var(--hairline))",
            }}
          />

          <div className="relative my-auto">
            <span className="mb-8 grid size-14 place-items-center rounded-2xl border border-primary/25 bg-primary/10 lg:size-16">
              <MailQuestion aria-hidden="true" className="size-7 text-primary-text" />
            </span>
            <h1 className="max-w-[16ch] text-[2rem] font-bold tracking-tight lg:text-[3rem] lg:leading-[1.05]">
              {RESEND_COPY.title}
            </h1>
            <div className="mt-7 h-px w-16 bg-primary" />
          </div>
        </aside>

        <main
          id="main-content"
          className="relative grid place-items-center overflow-hidden px-6 py-14 lg:px-16"
        >

          <div className="relative w-full max-w-[30rem]">
            <p className="text-[17px] leading-relaxed text-muted-foreground">
              {RESEND_COPY.message}
            </p>

            {notice ? (
              <RequestNotice tone={state === "sent" ? "success" : "error"} className="mt-8">
                {notice}
              </RequestNotice>
            ) : null}

            {done ? (
              <div className="mt-8">
                <BackToLogin />
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="resend-email" className="sr-only">
                    {RESEND_COPY.placeholder}
                  </label>
                  <input
                    id="resend-email"
                    type="email"
                    required
                    autoComplete="email"
                    disabled={busy}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={RESEND_COPY.placeholder}
                    className={FIELD_BASE}
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className={`${CTA_BASE} w-full`}
                >
                  {busy ? <Spinner /> : null}
                  {RESEND_COPY.submit}
                </button>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
